<template>
	<el-dialog
		:modelValue="open"
		@update:modelValue="$emit('update:open', $event)"
		:title="'Abstimmung kleiner Senat'"
		@close="handleClose"
		:before-close="(done) => dialogBeforeClose(done, waiting)"
	>
		<TheForm
			:model="request"
			:rules="rules"
			:waiting="waiting"
			:waitingMsg="texts.formErrors.waiting"
			:showErr="showErr"
			:errMsg="errMsg"
			ref="formRef"
			:id="id"
		>
			<el-form-item label="Zuständiger kleiner Senat">
				<el-input v-model="request.senatOption" disabled />
			</el-form-item>
			<el-row class="datetimepicker">
				<div class="datepicker-range__label">Abstimmungs-Zeitraum</div>
				<el-row>
					<el-form-item prop="dateRange">
						<div class="datepicker-range__labels">
							<label class="el-form-item__label">Startdatum</label>
							<label class="el-form-item__label">Enddatum</label>
						</div>
						<el-date-picker
							v-model="request.dateRange"
							type="daterange"
							:format="'DD.MM.YYYY'"
							range-separator=""
							start-placeholder="TT.MM.JJJJ"
							end-placeholder="TT.MM.JJJJ"
							:disabled-date="checkIfDateInPast"
						/>
					</el-form-item>
				</el-row>
			</el-row>
		</TheForm>

		<template #footer>
			<div class="button-panel">
				<TheButton type="danger" @buttonclick="send" :disabled="waiting" :loading="waiting">Abstimmung starten</TheButton>
				<TheButton type="default" @buttonclick="handleClose">Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue';
import TheForm from '../TheForm.vue';
import TheButton from '../TheButton.vue';
import { texts } from '../../utils/texts';
import type { FormInstance } from 'element-plus';
import { SendCaseRequest, SendCaseType } from '@/types/types';
import { checkIfDateInPast } from '@/utils/search/searchUtils';
import { validateForm, resetForm, dialogBeforeClose, getStartOfDayWithTime, getEndOfDayWithTime } from '@/utils/functions';
import { sendCase } from '@/requests/cases';
import { useStore } from '../../store';
import moment from 'moment';

export default defineComponent({
	name: 'DialogSendCaseSmallSenate',
	data() {
		return {
			request: {
				senatOption: this.nextSenateOption,
				dateRange: null,
			},
			rules: {
				dateRange: [{ required: true, message: texts.formErrors.dateRangeMissing, trigger: 'change' }],
			},
			id: 'sendCaseSmallSenateForm',
			waiting: false,
			showErr: false,
			errMsg: texts.cases.saveFailed,
		};
	},
	components: {
		TheForm,
		TheButton,
	},
	props: {
		open: {
			type: Boolean,
			required: true,
		},
		caseId: {
			type: String,
			required: true,
		},
		nextSenateOption: {
			type: String,
			required: true,
		},
	},
	setup() {
		const store = useStore();
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
			store,
		};
	},
	emits: ['close', 'update:open'],
	computed: {
		texts() {
			return texts;
		},
		isAbnahme() {
			return this.store.state.environment === 'abnahme';
		},
	},
	watch: {
		nextSenateOption(newVal) {
			this.request.senatOption = newVal;
		},
	},
	methods: {
		async send() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.waiting = true;

			let fromDate = '';
			if (this.request?.dateRange && this.request?.dateRange[0]) {
				fromDate = getStartOfDayWithTime(this.request.dateRange[0]);

				const now = moment();
				const cutoffHour = this.isAbnahme ? 9 : 10;
				const cutoffTime = moment().set({ hour: cutoffHour, minute: 0, second: 0, millisecond: 0 });
				const firstDate = moment(this.request.dateRange[0]);

				if (firstDate.isSame(now, 'day')) {
					// Vor der Cutoff-Zeit: setze auf Cutoff-Zeit (9 Uhr in Abnahme, 10 Uhr sonst)
					if (now.isBefore(cutoffTime)) {
						fromDate = cutoffTime.toISOString(true);
					}
					// Nach der Cutoff-Zeit: setze auf den aktuellen Timestamp
					else {
						fromDate = now.toISOString(true);
					}
				}
				// Andernfalls das ursprüngliche Datum beibehalten
				else {
					fromDate = getStartOfDayWithTime(this.request.dateRange[0], cutoffHour);
				}
			}

			let toDate = '';
			if (this.request?.dateRange && this.request?.dateRange[1]) {
				toDate = getEndOfDayWithTime(this.request.dateRange[1]);
			}

			const requestSendCase = {
				caseId: this.caseId,
				sendCaseType: SendCaseType.SmallSenate,
				roles: [
					{
						name: this.request.senatOption,
					},
				],
				fromDate: fromDate,
				toDate: toDate,
			} as SendCaseRequest;

			const result = await sendCase(requestSendCase);
			if (result.success) {
				this.handleClose(true);
			} else {
				this.showErr = true;
			}

			this.waiting = false;
		},
		handleClose(reload: Boolean) {
			resetForm(this.formRef, this.id);

			this.showErr = false;
			this.$emit('close', reload);
			this.$emit('update:open', false);
		},
		checkIfDateInPast,
		dialogBeforeClose,
	},
});
</script>
